import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Timer, CheckCircle2, XCircle, RotateCcw, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { QUIZ_DATA } from '@/lib/data';

const questions = QUIZ_DATA['course-1'];

export default function Quizzes() {
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  useEffect(() => {
    if (!started || showResult) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { setShowResult(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [started, showResult]);

  const handleNext = () => {
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);
    setSelected(null);
    if (current < questions.length - 1) {
      setCurrent(current + 1);
    } else {
      setShowResult(true);
    }
  };

  const score = answers.filter((a, i) => a === questions[i]?.correct).length;
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  const restart = () => {
    setStarted(false);
    setCurrent(0);
    setSelected(null);
    setAnswers([]);
    setShowResult(false);
    setTimeLeft(300);
  };

  if (!started) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        <h1 className="font-heading text-2xl font-bold">Quizzes</h1>
        <div className="bg-card rounded-xl border p-8 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold">Web Development Quiz</h2>
          <p className="text-muted-foreground text-sm mt-2">{questions.length} questions · 5 minutes</p>
          <Button onClick={() => setStarted(true)} className="mt-6 gap-2">
            Start Quiz <Timer className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
    );
  }

  if (showResult) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        <h1 className="font-heading text-2xl font-bold">Quiz Results</h1>
        <div className="bg-card rounded-xl border p-8 text-center max-w-lg mx-auto">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
            percentage >= 70 ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {percentage >= 70 ? (
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            ) : (
              <XCircle className="w-10 h-10 text-red-600" />
            )}
          </div>
          <h2 className="font-heading text-3xl font-bold">{percentage}%</h2>
          <p className="text-muted-foreground mt-1">{score}/{questions.length} correct answers</p>
          <p className="text-sm mt-3 font-medium">
            {percentage >= 70 ? '🎉 Congratulations! You passed!' : 'Keep learning and try again!'}
          </p>

          <div className="space-y-3 mt-6 text-left">
            {questions.map((q, i) => (
              <div key={q.id} className={`p-3 rounded-lg text-sm ${
                answers[i] === q.correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className="font-medium">{q.question}</p>
                <p className="text-xs mt-1">Your answer: {q.options[answers[i]] || 'Not answered'}</p>
                {answers[i] !== q.correct && (
                  <p className="text-xs text-green-700 mt-0.5">Correct: {q.options[q.correct]}</p>
                )}
              </div>
            ))}
          </div>

          <Button onClick={restart} className="mt-6 gap-2">
            <RotateCcw className="w-4 h-4" /> Retry Quiz
          </Button>
        </div>
      </motion.div>
    );
  }

  const q = questions[current];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">Web Development Quiz</h1>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-card rounded-lg border">
          <Timer className="w-4 h-4 text-muted-foreground" />
          <span className={`font-mono font-medium text-sm ${timeLeft < 60 ? 'text-red-600' : ''}`}>
            {mins}:{secs.toString().padStart(2, '0')}
          </span>
        </div>
      </div>

      <Progress value={((current + 1) / questions.length) * 100} className="h-2" />
      <p className="text-sm text-muted-foreground">Question {current + 1} of {questions.length}</p>

      <div className="bg-card rounded-xl border p-6 space-y-5">
        <h2 className="font-heading text-lg font-semibold">{q.question}</h2>
        <div className="space-y-3">
          {q.options.map((option, i) => (
            <button
              key={i}
              onClick={() => setSelected(i)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all text-sm ${
                selected === i
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/30 hover:bg-muted/50'
              }`}
            >
              <span className="font-medium mr-3 text-muted-foreground">{String.fromCharCode(65 + i)}.</span>
              {option}
            </button>
          ))}
        </div>
        <Button onClick={handleNext} disabled={selected === null} className="w-full">
          {current < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
        </Button>
      </div>
    </motion.div>
  );
}